import clsx from "clsx";
import { useState } from "react";
import { Badge, Button, Menu, Popover, Select, type BadgeTone, type ButtonSize, type IconName } from "@/components";
import { rememberedPort } from "./balance/serialTransport";
import type { ProtocolId } from "./balance/types";
import { useBalance, type UseBalance } from "./balance/useBalance";
import styles from "./BalanceConnect.module.css";

export interface BalanceConnectProps {
  size?: ButtonSize;
  /** Hide the simulator entry (production screens that don't need demos can turn it off). */
  allowSimulator?: boolean;
  /** A single trigger with connection details in a popover, for constrained toolbars. */
  compact?: boolean;
  /** Keep the compact trigger square on narrow screens; its accessible name includes the status. */
  iconOnly?: boolean;
  className?: string;
}

const TRANSPORT_NAMES = { serial: "USB", keyboard: "keyboard mode", simulator: "simulator" } as const;

function statusLook(balance: UseBalance): { tone: BadgeTone; icon: IconName; text: string } {
  switch (balance.status) {
    case "connected":
      if (balance.noData) return { tone: "warning", icon: "alertTriangle", text: "No data from the balance" };
      if (balance.error) return { tone: "danger", icon: "alertCircle", text: "Balance problem" };
      return {
        tone: "success",
        icon: "checkCircle",
        text: `Connected${balance.transport ? ` (${TRANSPORT_NAMES[balance.transport]})` : ""}`,
      };
    case "connecting":
      return { tone: "info", icon: "refresh", text: "Connecting…" };
    case "reconnecting":
      return { tone: "warning", icon: "alertTriangle", text: "Reconnecting…" };
    case "error":
      return { tone: "danger", icon: "alertCircle", text: "Balance problem" };
    default:
      return { tone: "neutral", icon: "scale", text: "No balance" };
  }
}

/** Connection status plus a menu to connect (USB, keyboard mode, simulator), tare, identify or disconnect. */
export function BalanceConnect({
  size = "md",
  allowSimulator = true,
  compact = false,
  iconOnly = false,
  className,
}: BalanceConnectProps) {
  const balance = useBalance();
  const [protocol, setProtocol] = useState<ProtocolId>(() => rememberedPort()?.protocol ?? "and");
  const [open, setOpen] = useState(false);
  const look = statusLook(balance);
  const live = balance.status === "connected";
  const reconnecting = balance.status === "reconnecting";
  const identity = [balance.identity.model, balance.identity.serial ? `SN ${balance.identity.serial}` : null]
    .filter(Boolean)
    .join(" · ");

  if (compact) {
    const connecting = balance.status === "connecting";
    return (
      <Popover
        open={open}
        onOpenChange={setOpen}
        title="Balance connection"
        align="end"
        className={styles.popover}
        trigger={
          <Button
            size={size}
            variant="ghost"
            icon={balance.error || balance.noData ? "alertTriangle" : "scale"}
            iconEnd={iconOnly ? undefined : "chevronDown"}
            aria-label={`Balance: ${look.text}`}
            title={`Balance: ${look.text}`}
            className={className}
          >
            {iconOnly ? undefined : "Balance"}
          </Button>
        }
      >
        <div className={styles.details}>
          <div role="status">
            <Badge tone={look.tone} icon={look.icon}>
              {look.text}
            </Badge>
          </div>
          {identity ? <p className={styles.identity}>{identity}</p> : null}
          {balance.status === "connected" && balance.transport === "serial" ? (
            <p className={styles.hint}>This tab reconnects to the balance automatically after a reload.</p>
          ) : null}
          {!live && !balance.serial.supported ? <p className={styles.hint}>{balance.serial.reason}</p> : null}
          {balance.transport === "keyboard" && live ? (
            <p className={styles.hint}>Press PRINT on the balance: it types the reading here.</p>
          ) : null}
          {balance.error ? (
            <p className={styles.error} role="status">
              {balance.error}
            </p>
          ) : null}
        </div>
        <div className={styles.actions}>
          {connecting ? <Button onClick={() => void balance.disconnect()}>Cancel connection</Button> : null}
          {reconnecting ? <Button onClick={() => void balance.disconnect()}>Disconnect</Button> : null}
          {live ? (
            <>
              {!balance.canWrite ? (
                <p className={styles.hint}>Tare and device details are controlled on the balance in keyboard mode.</p>
              ) : null}
              <Button icon="refresh" onClick={() => void balance.tare()} disabled={!balance.canWrite}>
                Tare (zero the vessel)
              </Button>
              <Button icon="info" onClick={() => void balance.identify()} disabled={!balance.canWrite}>
                Read model and serial number
              </Button>
              <Button icon="x" onClick={() => void balance.disconnect()}>
                Disconnect
              </Button>
            </>
          ) : (
            <>
              {balance.serial.supported ? (
                <Select<ProtocolId>
                  label="USB balance speaks"
                  value={protocol}
                  onValueChange={(value) => value && setProtocol(value)}
                  disabled={connecting}
                  options={[
                    { value: "and", label: "A&D (FX-i, GX, GF, EK)" },
                    { value: "mt-sics", label: "Mettler Toledo or Kern" },
                    { value: "generic", label: "Other (number lines)" },
                  ]}
                />
              ) : null}
              <Button
                icon="scale"
                disabled={!balance.serial.supported || connecting}
                onClick={() => void balance.connect("serial", { protocol })}
              >
                USB balance (Web Serial)…
              </Button>
              <Button icon="list" disabled={connecting} onClick={() => void balance.connect("keyboard")}>
                Keyboard mode (Quick USB)
              </Button>
              {allowSimulator ? (
                <Button icon="play" disabled={connecting} onClick={() => void balance.connect("simulator")}>
                  Simulator
                </Button>
              ) : null}
            </>
          )}
        </div>
        <Button variant="ghost" onClick={() => setOpen(false)}>
          Done
        </Button>
      </Popover>
    );
  }

  return (
    <div className={clsx(styles.connect, className)}>
      <div className={styles.row}>
        <Badge tone={look.tone} icon={look.icon}>
          {look.text}
        </Badge>
        {identity ? <span className={styles.identity}>{identity}</span> : null}
        <Menu.Root>
          <Menu.Trigger>
            <Button size={size} icon="scale" iconEnd="chevronDown">
              {live ? "Balance" : "Connect balance"}
            </Button>
          </Menu.Trigger>
          <Menu.Popup align="end">
            {live ? (
              <>
                <Menu.Item icon="refresh" onClick={() => void balance.tare()} disabled={!balance.canWrite}>
                  Tare (zero the vessel)
                </Menu.Item>
                <Menu.Item icon="info" onClick={() => void balance.identify()} disabled={!balance.canWrite}>
                  Read model and serial number
                </Menu.Item>
                <Menu.Separator />
                <Menu.Item icon="x" onClick={() => void balance.disconnect()}>
                  Disconnect
                </Menu.Item>
              </>
            ) : (
              <>
                {reconnecting ? (
                  <Menu.Item icon="x" onClick={() => void balance.disconnect()}>
                    Disconnect
                  </Menu.Item>
                ) : null}
                <Menu.Item
                  icon="scale"
                  disabled={!balance.serial.supported}
                  onClick={() => void balance.connect("serial", { protocol })}
                >
                  USB balance (Web Serial)…
                </Menu.Item>
                <Menu.Item icon="list" onClick={() => void balance.connect("keyboard")}>
                  Keyboard mode (Quick USB)
                </Menu.Item>
                {allowSimulator ? (
                  <Menu.Item icon="play" onClick={() => void balance.connect("simulator")}>
                    Simulator
                  </Menu.Item>
                ) : null}
                <Menu.Separator />
                <Menu.RadioGroup label="USB balance speaks" value={protocol} onValueChange={setProtocol}>
                  <Menu.RadioItem value="and">A&amp;D (FX-i, GX, GF, EK)</Menu.RadioItem>
                  <Menu.RadioItem value="mt-sics">Mettler Toledo or Kern</Menu.RadioItem>
                  <Menu.RadioItem value="generic">Other (number lines)</Menu.RadioItem>
                </Menu.RadioGroup>
              </>
            )}
          </Menu.Popup>
        </Menu.Root>
      </div>
      {!live && !balance.serial.supported ? <p className={styles.hint}>{balance.serial.reason}</p> : null}
      {balance.status === "connected" && balance.transport === "serial" ? (
        <p className={styles.hint}>This tab reconnects to the balance automatically after a reload.</p>
      ) : null}
      {balance.transport === "keyboard" && live ? (
        <p className={styles.hint}>Press PRINT on the balance: it types the reading here.</p>
      ) : null}
      {balance.error ? (
        <p className={styles.error} role="status">
          {balance.error}
        </p>
      ) : null}
    </div>
  );
}
